import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import { VariableSizeList as List, ListChildComponentProps } from 'react-window';
import MenuItem from '@material-ui/core/MenuItem/MenuItem';
import Menu from '@material-ui/core/Menu';
import AutoSizer from 'react-virtualized-auto-sizer';
import { S } from './PlannedRoutesPanel.styles';
import { PlannedRoutesPanelProps } from './PlannedRoutesPanel.interfaces';
import { FormattedMessage } from 'react-intl';
import { CloseButton } from 'shared/Buttons/CloseButton/CloseButton';
import { RouteCard } from './RouteCard/RouteCard';
import { getPlannedRoutesLoadingStateSelector, guidePlannedRoutes } from 'store/UserStore/selectors';
import { PlannedRouteExt } from 'commonTypes/GuideUse/guideGeo';
import { WaiterPopup } from 'shared/WaiterAnimation/WaiterPopup';
import { theme } from 'theme';

type ItemData = { plannedRoutes: PlannedRouteExt[] | null };

const PlannedRoutesPanelComponent: React.FC<PlannedRoutesPanelProps> = ({
  isSavingPlannedRoutes,
  onRouteChanged,
  onRouteEditClick,
  onRouteDeleteClick,
  onCloseClick
}) => {

  const plannedRoutes = useSelector(guidePlannedRoutes);
  const itemData = useMemo<ItemData>(() => ({ plannedRoutes }), [plannedRoutes]);
  const isPlannedRoutesLoading = useSelector(getPlannedRoutesLoadingStateSelector);

  const [menuPosition, setMenuPosition] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<PlannedRouteExt | null>(null);

  const itemPadding = parseFloat(theme.plannedRoute.sizes.padding);
  const titleSize = parseFloat(theme.plannedRoute.sizes.titleFont) * 1.55;
  const titleMaxLines = theme.plannedRoute.sizes.titleMaxLines;
  let descriptionSize = parseFloat(theme.plannedRoute.sizes.descriptionFont) * 1.55;

  const descriptionMaxLines = theme.plannedRoute.sizes.descriptionMaxLines;

  const handleMenuButtonClick = (
    event: React.MouseEvent<HTMLElement>,
    plannedRoute: PlannedRouteExt
  ) => {
    event.preventDefault();
    setSelectedRoute(plannedRoute);
    setMenuPosition({
        mouseX: event.clientX,
        mouseY: event.clientY,
      });    
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
    setSelectedRoute(null);
  };

  const handleMenuItemClick = (action: string, plannedRoute: PlannedRouteExt | null) => {
    switch (action) {
      case "edit":
        if (plannedRoute && onRouteEditClick){
          onRouteEditClick(plannedRoute);
        }
        break;
    
      case "delete":
        if (plannedRoute && onRouteDeleteClick){
          onRouteDeleteClick(plannedRoute);
        }
        break;
    }
    handleMenuClose();
  };  

  const Row = React.memo(
    ({ index, style, data }: ListChildComponentProps<PlannedRouteExt[] | null>) => {
      if (data != null) {
        const route = data[index]
        return (
          <div style={style}>
            {route && <RouteCard route={route} onRouteChanged={onRouteChanged} onMenuButtonClick={handleMenuButtonClick} />}
          </div>
        );
      }
      return null;
  },
  (prev, next) =>
      prev.data != null && next.data != null &&
      prev.index === next.index &&
      prev.data[prev.index] === next.data[prev.index] &&
      prev.style === next.style
  );
 
  const getItemSize = useCallback((index: number) => {
      if (itemData.plannedRoutes){
          const title = itemData?.plannedRoutes[index].routeName;
          const description = itemData?.plannedRoutes[index].routeDescription || null;

          let titleLines = Math.ceil((title?.length ?? 0) / 38);
          if (titleLines > titleMaxLines){
            titleLines = titleMaxLines;
          }
          else{
            titleLines = 1;
          }
          let descriptionLines = 0;
          if (description != null && description.length > 0){
            descriptionLines = Math.ceil(description.length / 46);
            if (descriptionLines > descriptionMaxLines){
              descriptionLines = descriptionMaxLines;
            }
          }
          return itemPadding * 2 +  titleLines * titleSize + descriptionLines * descriptionSize + 2 * descriptionSize;
      }
      return 0;
  }, [descriptionSize, itemData.plannedRoutes]);

  return (
    <S.Container>
      <S.Header>
        <S.Title><FormattedMessage id="planned_routes"/></S.Title>
        <CloseButton onClick={onCloseClick} />
      </S.Header>
        {!isPlannedRoutesLoading && itemData?.plannedRoutes !== null ? (
          <S.ListContainer>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  height={height}
                  itemCount={itemData?.plannedRoutes?.length || 0}
                  itemSize={getItemSize}
                  width={width}
                  itemData={itemData.plannedRoutes}>
                  {Row}
                </List>
              )}
            </AutoSizer>

            {/* Context menu */}
            <Menu
              open={Boolean(menuPosition)}
              onClose={handleMenuClose}
              anchorReference="anchorPosition"
              anchorPosition={
                menuPosition !== null
                  ? { top: menuPosition.mouseY, left: menuPosition.mouseX }
                  : undefined
              }
            >
              <MenuItem onClick={() => handleMenuItemClick("edit", selectedRoute)}><FormattedMessage id={"edit"}/></MenuItem>
              <MenuItem onClick={() => handleMenuItemClick("delete", selectedRoute)}><FormattedMessage id={"delete"}/></MenuItem>
            </Menu>
          </S.ListContainer>
        ) : ( 
          <WaiterPopup open={true} />
        )}
        {isSavingPlannedRoutes && <WaiterPopup open={true} />}
    </S.Container>
  );
};

export const PlannedRoutesPanel = memo(PlannedRoutesPanelComponent, isEqual);
